import {Box, Typography} from '@mui/material';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {useEffect, useState, type FC} from 'react';
import {useSelector} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';
import {jobDetailService} from '@jobseeker/common-core/services';
import {deferTask, openUrl} from '@jobseeker/common-core/utils/common';

const isHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

const ACCENT = isHirist
  ? {
      bg: '#FFF3EE',
      border: '#F8D5C7',
      shadowRgb: '153,64,26',
      iconBg: '#FBDDD0',
      icon: '#F0491E',
      button: '#FF6A3D',
      buttonHover: '#e85a2e',
    }
  : {
      bg: '#F4FAF6',
      border: '#CFE3D6',
      shadowRgb: '53,122,86',
      iconBg: '#DCEDE3',
      icon: '#49966E',
      button: '#49966E',
      buttonHover: '#357A56',
    };

const IconBadge: FC = () => (
  <Box flexShrink={0} width={{xxs: 44, md: 44}} height={{xxs: 56, md: 44}}>
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none">
      <rect width="44" height="44" rx="22" fill={ACCENT.iconBg} />
      <circle cx="22" cy="22" r="9" stroke={ACCENT.icon} strokeWidth="2" />
      <path
        d="M22 16V22L26 24"
        stroke={ACCENT.icon}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const PendingApplicationBanner: FC = () => {
  const isLoggedIn = useSelector((state: AppState) => state.auth.isLoggedIn);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) return;
    jobDetailService.getPendingJobsCount().then(response => {
      setPendingCount(response?.data?.count || 0);
    });
  }, [isLoggedIn]);

  if (pendingCount <= 0) return null;

  return (
    <Box
      sx={{
        mb: 3.5,
        ml: 2,
        width: {xxs: '92%', md: '94%', lg: '95%', xl: '96%'},
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={isHirist ? 2 : {xxs: 2, md: 3}}
        flexWrap={isHirist ? 'wrap' : {xxs: 'wrap', md: 'nowrap'}}
        bgcolor={ACCENT.bg}
        border={`1px solid ${ACCENT.border}`}
        borderRadius="16px"
        px={isHirist ? 3 : {xxs: 2, md: 2.5}}
        py={isHirist ? 2.25 : 2}
        boxShadow={`0px 4px 12px 0px rgba(${ACCENT.shadowRgb},0.1)`}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          {...(!isHirist && {flex: 1, minWidth: 0})}
        >
          <IconBadge />
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography fontWeight={600} fontSize="16px" color="#1A1A1A">
              Pending application
            </Typography>
            {isHirist ? (
              <Typography fontSize="14px" color="#5C5C5C">
                You have {pendingCount} applications in progress — complete your
                profile to get noticed.
              </Typography>
            ) : (
              <Typography fontSize="14px" lineHeight="20px" color="#5C5C5C">
                You have {pendingCount}{' '}
                {pendingCount === 1 ? 'application' : 'applications'} in
                progress. Complete your profile to get noticed.
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          onClick={() =>
            deferTask(() =>
              openUrl(
                '/registration/addPersonalDetails?ref=MetaJobfeed',
                '_self'
              )
            )
          }
          sx={{
            bgcolor: ACCENT.button,
            color: '#fff',
            px: isHirist ? 2.5 : 2,
            py: isHirist ? 1.375 : {xxs: 1.375, md: 1},
            borderRadius: isHirist ? '10px' : '8px',
            fontWeight: 600,
            fontSize: isHirist ? '14px' : '13px',
            whiteSpace: 'nowrap',
            ...(!isHirist && {flexShrink: 0}),
            textTransform: 'none',
            width: {xxs: '100%', md: 'auto'},
            textAlign: 'center',
            justifyContent: 'center',
            ':hover': {
              bgcolor: ACCENT.buttonHover,
            },
          }}
        >
          Complete application
        </Button>
      </Box>
    </Box>
  );
};

export default PendingApplicationBanner;
